import React from "react";
import { motion } from "framer-motion";
import about2 from "../../assets/about2.jpeg";
import about3 from "../../assets/about3.jpeg";

const highlights = [
  {
    title: "Tim Ahli",
    desc: "Berpengalaman dengan tim profesional di bidangnya",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
      </svg>
    ),
  },
  {
    title: "Patuh Regulasi",
    desc: "Sesuai standar lingkungan & pemerintah",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const AboutUs = () => {
  return (
    <section
      id="AboutUs"
      className="relative py-20 md:py-28 bg-gradient-mesh overflow-hidden"
    >
      {/* Decorative orb */}
      <div className="absolute -top-20 right-0 w-72 h-72 bg-brand-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-10 w-80 h-80 bg-eco-200/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row gap-12 lg:gap-16 items-center">
          {/* Image Collage */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1 relative w-full"
          >
            <div className="grid grid-cols-2 gap-4 lg:gap-6 relative">
              <motion.div
                whileHover={{ scale: 1.03, rotate: -1 }}
                transition={{ duration: 0.4 }}
                className="relative rounded-3xl overflow-hidden shadow-strong h-80 lg:h-96"
              >
                <img
                  src={about3}
                  alt="Team working"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-900/30 to-transparent" />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03, rotate: 1 }}
                transition={{ duration: 0.4 }}
                className="relative rounded-3xl overflow-hidden shadow-strong h-[28rem] lg:h-[32rem] mt-8"
              >
                <img
                  src={about2}
                  alt="Team brainstorming"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-bl from-eco-900/30 to-transparent" />
              </motion.div>
            </div>

            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl shadow-strong p-5 flex items-center gap-4 border border-slate-100"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 font-oswald">
                  100%
                </div>
                <div className="text-xs text-slate-600 font-overpass">
                  Komitmen Kualitas
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-brand-50 text-brand-700 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Tentang Kami
            </span>

            <h2 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-6">
              Didorong oleh{" "}
              <span className="text-gradient">semangat dan keahlian</span> di
              bidang kami
            </h2>

            <div className="font-overpass space-y-4 text-slate-600 text-base lg:text-lg leading-relaxed mb-8">
              <p className="text-justify">
                Defra adalah perusahaan yang menyediakan layanan perizinan
                limbah cair di Indonesia, mencakup analisis lingkungan dan
                pengurusan dokumen perizinan. Kami memastikan kepatuhan terhadap
                regulasi dengan dukungan tim ahli berpengalaman.
              </p>
              <p className="text-justify">
                Komitmen kami adalah membantu industri beroperasi secara efisien
                dan ramah lingkungan. Hubungi kami untuk layanan perizinan
                limbah cair yang cepat dan tepat.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15 }}
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-100 shadow-soft card-hover"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-eco-500 text-white flex items-center justify-center">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-oswald font-semibold text-slate-900">
                      {item.title}
                    </h4>
                    <p className="text-sm text-slate-600 font-overpass">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
